import React from "react";
import { BookingModal } from "@/components/Scheduler/schedule/BookingModal";
import { GoogleEventDetailsModal } from "@/components/Calendar/schedule/GoogleEventDetailsModal";
import { BookingCard } from "./BookingCard";
import { BlockOutCard } from "./BlockOutCard";
import { TaskCard } from "./TaskCard";
import {
  type Booking,
  type BlockOut,
  type Task,
  useScheduleData,
  type UseScheduleDataReturn,
} from "@/components/Calendar/scheduleData";

type GoogleCalendarEvent = {
  id: string;
  summary: string;
  description?: string;
  date: string;
  time: string;
  duration: number;
  htmlLink?: string;
};

interface TimeSlotCellProps {
  dateString: string;
  hour: number;
  hourBookings: Booking[];
  hourBlockOuts: BlockOut[];
  hourTasks?: Task[];
  serviceColors: Record<string, string>;
  serviceColorsFromData?: Record<string, string>;
  serviceNameMap?: Record<string, string>;
  serviceColorMap?: Record<string, string>;
  onCreateBooking: (bookingData: any) => void;
  onCreateBookingWithDefaults?: (options: {
    date: string;
    time?: string;
    pharmacist?: string;
    serviceType?: string;
  }) => void;
  onEditBooking: (bookingId: string, bookingData: any) => void;
  onDeleteBooking: (bookingId: string) => void;
  onEditBlockOut?: (blockOutId: string, blockOut: BlockOut) => void;
  onEditTask?: (taskId: string, task: Task) => void;
  pharmacists: string[];
  rooms: string[];
  size?: "week" | "day";
  isDarkMode?: boolean;
  onBookingDrop?: (booking: Booking, newDate: string, newTime?: string) => void;
  onDurationChange?: (bookingId: string, newDuration: number) => void;
  onBlockOutDurationChange?: (blockOutId: string, newEndTime: string) => void;
  isNonWorkingHours?: boolean;
  hourGoogleEvents?: GoogleCalendarEvent[];
  weekGoogleEventsByDay?: Record<string, GoogleCalendarEvent[]>;
  // New props for week view with day grouping
  weekBookingsByDay?: Record<string, Booking[]>;
  weekBlockOutsByDay?: Record<string, BlockOut[]>;
  weekTasksByDay?: Record<string, Task[]>;
  weekDays?: Date[];
  nonWorkingHoursByDay?: Record<string, boolean>;
  // Props for pre-selecting based on column mode
  columnMode?: "practitioner" | "day" | "services" | "room" | "day-practitioner";
  columnValue?: string;
  notifiedBookingId?: string | null;
  notificationType?: "message" | "check";
  onStartConsultation?: (booking: Booking) => void;
}

export function TimeSlotCell({
  dateString,
  hour,
  hourBookings,
  hourBlockOuts,
  hourTasks = [],
  serviceColors,
  serviceColorsFromData,
  serviceNameMap = {},
  serviceColorMap = {},
  onCreateBooking,
  onCreateBookingWithDefaults,
  onEditBooking,
  onDeleteBooking,
  onEditBlockOut,
  onEditTask,
  onStartConsultation,
  pharmacists,
  rooms,
  size = "week",
  isDarkMode = false,
  onBookingDrop,
  onDurationChange,
  onBlockOutDurationChange,
  isNonWorkingHours = false,
  hourGoogleEvents = [],
  weekGoogleEventsByDay,
  weekBookingsByDay,
  weekBlockOutsByDay,
  weekTasksByDay,
  weekDays,
  nonWorkingHoursByDay,
  columnMode,
  columnValue,
  notifiedBookingId,
  notificationType = "check",
}: TimeSlotCellProps) {
  const s = useScheduleData();
  const { resources = [] } = (s || {}) as Partial<UseScheduleDataReturn>;

  const [isDragOver, setIsDragOver] = React.useState(false);
  const [dragOverDayIndex, setDragOverDayIndex] = React.useState<number | null>(
    null,
  );
  const [selectedGoogleEvent, setSelectedGoogleEvent] =
    React.useState<any>(null);
  const [isGoogleEventModalOpen, setIsGoogleEventModalOpen] =
    React.useState(false);
  const formatTimeSlot = (timeSlot: number) => {
    const slotHour = Math.floor(timeSlot);
    const slotMinutes = timeSlot % 1 === 0 ? 0 : 30;

    return `${slotHour.toString().padStart(2, "0")}:${slotMinutes
      .toString()
      .padStart(2, "0")}`;
  };
  const initialTime = formatTimeSlot(hour);

  const isPastDate = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(`${dateStr}T00:00:00`);
    return date.getTime() < today.getTime();
  };

  const handleDragOver = (e: React.DragEvent) => {
    // Drag and drop disabled
    return;
    /*
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
    e.dataTransfer.dropEffect = "move";
    */
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Drag and drop disabled
    return;
    /*
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    */
  };

  const handleDrop = (e: React.DragEvent) => {
    // Drag and drop disabled
    return;
    /*
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const bookingData = e.dataTransfer.getData("application/json");
    if (bookingData && onBookingDrop) {
      const booking = JSON.parse(bookingData) as Booking;
      // Trigger drop with new date and time
      const newTime = formatTimeSlot(hour);
      onBookingDrop(booking, dateString, newTime);
    }
    */
  };

  const canClickSlot = !isNonWorkingHours && !isPastDate(dateString);

  if (size === "day") {
    return (
      <>
        <BookingModal
          trigger={
            <div
              className={`flex-1 p-3 min-h-[56px] transition-all duration-200 relative overflow-visible ${
                isNonWorkingHours || isPastDate(dateString)
                  ? isDarkMode
                    ? "bg-gray-800 cursor-not-allowed pointer-events-none"
                    : "bg-gray-200 cursor-not-allowed pointer-events-none"
                  : isDragOver
                    ? isDarkMode
                      ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                      : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                    : isDarkMode
                      ? "hover:bg-gray-700 cursor-pointer"
                      : "hover:bg-gray-50 cursor-pointer"
              }`}
              style={
                isNonWorkingHours || isPastDate(dateString)
                  ? {
                      backgroundImage: `repeating-linear-gradient(
                      45deg,
                      transparent,
                      transparent 10px,
                      ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 10px,
                      ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 20px
                    )`,
                    }
                  : undefined
              }
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div
                className="relative overflow-visible"
                style={{
                  display: hourBookings.length > 0 ? "grid" : "block",
                  gridTemplateColumns:
                    hourBookings.length > 0
                      ? `repeat(${hourBookings.length}, 1fr)`
                      : undefined,
                  gap: hourBookings.length > 0 ? "8px" : undefined,
                }}
              >
                {hourBookings.map((booking) => (
                  <div key={booking.id} className="relative min-w-0 w-full">
                    <BookingCard
                      booking={booking}
                      serviceColors={serviceColors}
                      serviceNameMap={serviceNameMap}
                      serviceColorMap={serviceColorMap}
                      onEdit={onEditBooking}
                      onDelete={onDeleteBooking}
                      onStartConsultation={onStartConsultation}
                      pharmacists={pharmacists}
                      rooms={rooms}
                      size="large"
                      isDarkMode={isDarkMode}
                      enableResize={true}
                      onDurationChange={onDurationChange}
                      showNotification={notifiedBookingId === booking.id}
                      notificationType={notificationType}
                    />
                  </div>
                ))}

                {hourGoogleEvents.length > 0 && (
                  <div
                    className="grid gap-2 mt-2"
                    style={{
                      gridTemplateColumns: `repeat(${hourGoogleEvents.length}, 1fr)`,
                    }}
                  >
                    {hourGoogleEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedGoogleEvent(event);
                          setIsGoogleEventModalOpen(true);
                        }}
                        className={`rounded-md border p-2 text-[11px] font-medium overflow-hidden truncate cursor-pointer transition-all hover:shadow-md ${
                          isDarkMode
                            ? "bg-blue-950/80 border-blue-700 text-white hover:bg-blue-900/90"
                            : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                                isDarkMode
                                  ? "bg-red-400 text-black"
                                  : "bg-red-600 text-white"
                              }`}
                              title="Google event"
                            >
                              G
                            </span>
                            <div className="truncate min-w-0">
                              {event.summary}
                            </div>
                          </div>
                        </div>

                        <div className="mt-1 text-[10px]">
                          {event.time} • {event.duration}m
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Block-out periods */}
                <div
                  style={{
                    display: hourBlockOuts.length > 0 ? "grid" : "block",
                    gridTemplateColumns:
                      hourBlockOuts.length > 0
                        ? `repeat(${hourBlockOuts.length}, 1fr)`
                        : undefined,
                    gap: hourBlockOuts.length > 0 ? "8px" : undefined,
                    marginTop: hourBookings.length > 0 ? "8px" : "0",
                  }}
                >
                  {hourBlockOuts.map((blockOut) => (
                    <div key={blockOut.id} className="min-w-0">
                      <BlockOutCard
                        blockOut={blockOut}
                        size="large"
                        isDarkMode={isDarkMode}
                        onEdit={onEditBlockOut}
                        enableResize={true}
                        onDurationChange={onBlockOutDurationChange}
                      />
                    </div>
                  ))}
                </div>

                {/* Tasks */}
                <div
                  style={{
                    display: hourTasks.length > 0 ? "grid" : "block",
                    gridTemplateColumns:
                      hourTasks.length > 0
                        ? `repeat(${hourTasks.length}, 1fr)`
                        : undefined,
                    gap: hourTasks.length > 0 ? "8px" : undefined,
                    marginTop:
                      hourBookings.length > 0 || hourBlockOuts.length > 0
                        ? "8px"
                        : "0",
                  }}
                >
                  {hourTasks.map((task) => (
                    <div key={task.id} className="min-w-0">
                      <TaskCard
                        task={task}
                        size="large"
                        isDarkMode={isDarkMode}
                        onEdit={onEditTask}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          }
          onSave={onCreateBooking}
          pharmacists={pharmacists}
          rooms={rooms}
          initialDate={dateString}
          initialTime={initialTime}
          initialPharmacist={
            columnMode === "practitioner" ? columnValue : undefined
          }
          initialServiceType={
            columnMode === "services" ? (columnValue as any) : undefined
          }
          isDarkMode={isDarkMode}
        />
        <GoogleEventDetailsModal
          isDarkMode={isDarkMode}
          event={selectedGoogleEvent}
          isOpen={isGoogleEventModalOpen}
          onClose={() => setIsGoogleEventModalOpen(false)}
        />
      </>
    );
  }

  // Day-Practitioner mode (Practitioners nested under day columns)
  if (columnMode === "day-practitioner") {
    const dayDateString = dateString || columnValue;
    const allBookings = weekBookingsByDay?.[dayDateString] || hourBookings || [];
    const allBlockOuts = weekBlockOutsByDay?.[dayDateString] || hourBlockOuts || [];
    const allTasks = weekTasksByDay?.[dayDateString] || [];
    const allGoogleEvents = weekGoogleEventsByDay?.[dayDateString] || [];
    const displayList = pharmacists && pharmacists.length > 0 ? pharmacists : ["Unassigned"];

    return (
      <>
        <div
          className={`grid border-r min-h-14 transition-all duration-200 relative overflow-visible ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
          style={{ gridTemplateColumns: `repeat(${displayList.length}, 1fr)` }}
        >
          {displayList.map((pharmacistName, pIndex) => {
            const normP = (pharmacistName || "").trim().toLowerCase();
            const pResource = (resources || []).find(
              (r: any) => r.name && r.name.trim().toLowerCase() === normP,
            );
            const pResId = (pResource?.id || "").trim().toLowerCase();

            // Filter bookings for this practitioner
            const dayBookings = allBookings.filter((b) => {
              const normB = (b.pharmacist || "").trim().toLowerCase();
              return normB === normP || (pResId && normB === pResId);
            });

            // Filter blockouts for this practitioner
            const dayBlockOuts = allBlockOuts.filter((bo) => {
              if (!bo.affectedPharmacists || bo.affectedPharmacists.length === 0) {
                return true;
              }
              return bo.affectedPharmacists.some((p: string) => {
                const normAff = p.trim().toLowerCase();
                return normAff === normP || (pResId && normAff === pResId);
              });
            });

            // Filter tasks for this practitioner
            const dayTasks = allTasks.filter((t) => {
              const normAssigned = (t.assignedTo || "").trim().toLowerCase();
              return (
                normAssigned === normP ||
                (pResId && normAssigned === pResId) ||
                !t.assignedTo ||
                t.assignedTo === "" ||
                t.assignedTo === "Unassigned"
              );
            });

            const dayGoogleEvents = allGoogleEvents;
            const isNonWorking = nonWorkingHoursByDay?.[dayDateString] || false;
            const isDayDragOver = dragOverDayIndex === pIndex;

            const handleDayDragOver = (e: React.DragEvent) => {
              // Drag and drop disabled
              return;
              /*
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(pIndex);
              e.dataTransfer.dropEffect = "move";
              */
            };

            const handleDayDragLeave = (e: React.DragEvent) => {
              // Drag and drop disabled
              return;
              /*
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);
              */
            };

            const handleDayDrop = (e: React.DragEvent) => {
              // Drag and drop disabled
              return;
              /*
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);

              const bookingData = e.dataTransfer.getData("application/json");
              if (bookingData && onBookingDrop) {
                const booking = JSON.parse(bookingData) as Booking;
                const newTime = formatTimeSlot(hour);
                onBookingDrop(booking, dayDateString, newTime);
              }
              */
            };

            return (
              <div
                key={pIndex}
                className={`p-1.5 border-r last:border-r-0 transition-all duration-200 relative overflow-visible flex flex-col gap-1 ${
                  isNonWorking || isPastDate(dayDateString)
                    ? isDarkMode
                      ? "bg-gray-800 cursor-not-allowed border-gray-700"
                      : "bg-gray-200 cursor-not-allowed border-gray-300"
                    : isDayDragOver
                      ? isDarkMode
                        ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                        : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                      : isDarkMode
                        ? "border-gray-700 hover:bg-gray-700/50 cursor-pointer"
                        : "border-gray-300 hover:bg-gray-100 cursor-pointer"
                }`}
                style={
                  isNonWorking || isPastDate(dayDateString)
                    ? {
                        backgroundImage: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 12px
                      )`,
                      }
                    : undefined
                }
                title={
                  isPastDate(dayDateString)
                    ? `Cannot create booking on past dates`
                    : isNonWorking
                      ? `Not available - ${dayDateString}`
                      : `Click to add booking for ${pharmacistName} on ${dayDateString}`
                }
                onClick={
                  isNonWorking || isPastDate(dayDateString)
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        const timeString = formatTimeSlot(hour);
                        if (onCreateBookingWithDefaults) {
                          onCreateBookingWithDefaults({
                            date: dayDateString,
                            time: timeString,
                            pharmacist: pharmacistName !== "Unassigned" ? pharmacistName : undefined,
                          });
                        } else {
                          onCreateBooking({
                            date: dayDateString,
                            time: timeString,
                          });
                        }
                      }
                }
                onDragOver={handleDayDragOver}
                onDragLeave={handleDayDragLeave}
                onDrop={handleDayDrop}
              >
                {/* Bookings */}
                {dayBookings.length > 0 && (
                  <div
                    className="flex flex-row gap-0.5 w-full items-start"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayBookings.length}, 1fr)`,
                      gap: "2px",
                    }}
                  >
                    {dayBookings.map((booking) => (
                      <div key={booking.id} className="min-w-0 w-full">
                        <BookingCard
                          booking={booking}
                          serviceColors={serviceColors}
                          serviceNameMap={serviceNameMap}
                          serviceColorMap={serviceColorMap}
                          onEdit={onEditBooking}
                          onDelete={onDeleteBooking}
                          onStartConsultation={onStartConsultation}
                          pharmacists={pharmacists}
                          rooms={rooms}
                          size="micro"
                          isDarkMode={isDarkMode}
                          enableResize={true}
                          onDurationChange={onDurationChange}
                          showNotification={notifiedBookingId === booking.id}
                          notificationType={notificationType}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Google events */}
                {dayGoogleEvents.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayGoogleEvents.length}, 1fr)`,
                      gap: "2px",
                      marginTop: dayBookings.length > 0 ? "4px" : "0",
                    }}
                  >
                    {dayGoogleEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedGoogleEvent(event);
                          setIsGoogleEventModalOpen(true);
                        }}
                        className={`rounded-md border p-1 text-[11px] font-medium overflow-hidden truncate cursor-pointer transition-all hover:shadow-md ${
                          isDarkMode
                            ? "bg-blue-950/80 border-blue-700 text-white hover:bg-blue-900/90"
                            : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                        }`}
                      >
                        <div className="truncate">{event.summary}</div>
                        <div className="mt-1 text-[10px]">
                          {event.time} • {event.duration}m
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Block-out periods */}
                {dayBlockOuts.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayBlockOuts.length}, 1fr)`,
                      gap: "2px",
                      marginTop: dayBookings.length > 0 ? "4px" : "0",
                    }}
                  >
                    {dayBlockOuts.map((blockOut) => (
                      <div key={blockOut.id} className="min-w-0">
                        <BlockOutCard
                          blockOut={blockOut}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditBlockOut}
                          enableResize={true}
                          onDurationChange={onBlockOutDurationChange}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Tasks */}
                {dayTasks.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayTasks.length}, 1fr)`,
                      gap: "2px",
                      marginTop:
                        dayBookings.length > 0 || dayBlockOuts.length > 0
                          ? "4px"
                          : "0",
                    }}
                  >
                    {dayTasks.map((task) => (
                      <div key={task.id} className="min-w-0">
                        <TaskCard
                          task={task}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditTask}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <GoogleEventDetailsModal
          isDarkMode={isDarkMode}
          event={selectedGoogleEvent}
          isOpen={isGoogleEventModalOpen}
          onClose={() => setIsGoogleEventModalOpen(false)}
        />
      </>
    );
  }

  // Week view with day grouping
  if (size === "week" && weekBookingsByDay && weekDays) {
    const formatDateFromDate = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    const numDays = weekDays.length;

    return (
      <>
        <div
          className={`grid border-r min-h-14 transition-all duration-200 relative overflow-visible ${
            isDarkMode ? "border-gray-600" : "border-gray-200"
          }`}
          style={{ gridTemplateColumns: `repeat(${numDays}, 1fr)` }}
        >
          {weekDays.map((day, dayIndex) => {
            const dayDateString = formatDateFromDate(day);
            const dayBookings = weekBookingsByDay[dayDateString] || [];
            const dayBlockOuts = weekBlockOutsByDay?.[dayDateString] || [];
            const dayTasks = weekTasksByDay?.[dayDateString] || [];
            const dayGoogleEvents =
              weekGoogleEventsByDay?.[dayDateString] || [];
            const isNonWorking = nonWorkingHoursByDay?.[dayDateString] || false;
            const isDayDragOver = dragOverDayIndex === dayIndex;

            const handleDayDragOver = (e: React.DragEvent) => {
              // Drag and drop disabled
              return;
              /*
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(dayIndex);
              e.dataTransfer.dropEffect = "move";
              */
            };

            const handleDayDragLeave = (e: React.DragEvent) => {
              // Drag and drop disabled
              return;
              /*
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);
              */
            };

            const handleDayDrop = (e: React.DragEvent) => {
              // Drag and drop disabled
              return;
              /*
              if (isNonWorking) return;
              e.preventDefault();
              e.stopPropagation();
              setDragOverDayIndex(null);

              const bookingData = e.dataTransfer.getData("application/json");
              if (bookingData && onBookingDrop) {
                const booking = JSON.parse(bookingData) as Booking;
                // Trigger drop with new date and time
                const newTime = formatTimeSlot(hour);
                onBookingDrop(booking, dayDateString, newTime);
              }
              */
            };

            return (
              <div
                key={dayIndex}
                className={`p-1.5 border-r last:border-r-0 transition-all duration-200 relative overflow-visible flex flex-col gap-1 ${
                  isNonWorking || isPastDate(dayDateString)
                    ? isDarkMode
                      ? "bg-gray-800 cursor-not-allowed border-gray-700"
                      : "bg-gray-200 cursor-not-allowed border-gray-300"
                    : isDayDragOver
                      ? isDarkMode
                        ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                        : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                      : isDarkMode
                        ? "border-gray-700 hover:bg-gray-700/50 cursor-pointer"
                        : "border-gray-300 hover:bg-gray-100 cursor-pointer"
                }`}
                style={
                  isNonWorking || isPastDate(dayDateString)
                    ? {
                        backgroundImage: `repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 6px,
                        ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 12px
                      )`,
                      }
                    : undefined
                }
                title={
                  isPastDate(dayDateString)
                    ? `Cannot create booking on past dates`
                    : isNonWorking
                      ? `Not available - ${dayDateString}`
                      : `Click to add booking for ${dayDateString}`
                }
                onClick={
                  isNonWorking || isPastDate(dayDateString)
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        const timeString = `${hour.toString().padStart(2, "0")}:00`;

                        // Use enhanced booking creation with pre-selection when available
                        if (onCreateBookingWithDefaults) {
                          const options: any = {
                            date: dayDateString,
                            time: timeString,
                          };

                          // Pre-select based on column mode
                          if (columnMode === "practitioner" && columnValue) {
                            options.pharmacist = columnValue;
                          } else if (columnMode === "services" && columnValue) {
                            options.serviceType = columnValue;
                          }

                          onCreateBookingWithDefaults(options);
                        } else {
                          // Fallback to legacy method
                          onCreateBooking({
                            date: dayDateString,
                            time: timeString,
                          });
                        }
                      }
                }
                onDragOver={handleDayDragOver}
                onDragLeave={handleDayDragLeave}
                onDrop={handleDayDrop}
              >
                {/* Show booking cards stacked horizontally like Google Calendar */}
                {dayBookings.length > 0 && (
                  <div
                    className="flex flex-row gap-0.5 w-full items-start"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayBookings.length}, 1fr)`,
                      gap: "2px",
                    }}
                  >
                    {dayBookings.map((booking) => (
                      <div key={booking.id} className="min-w-0 w-full">
                        <BookingCard
                          booking={booking}
                          serviceColors={serviceColors}
                          serviceNameMap={serviceNameMap}
                          serviceColorMap={serviceColorMap}
                          onEdit={onEditBooking}
                          onDelete={onDeleteBooking}
                          onStartConsultation={onStartConsultation}
                          pharmacists={pharmacists}
                          rooms={rooms}
                          size="micro"
                          isDarkMode={isDarkMode}
                          enableResize={true}
                          onDurationChange={onDurationChange}
                          showNotification={notifiedBookingId === booking.id}
                          notificationType={notificationType}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {dayGoogleEvents.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayGoogleEvents.length}, 1fr)`,
                      gap: "2px",
                      marginTop: dayBookings.length > 0 ? "4px" : "0",
                    }}
                  >
                    {dayGoogleEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => {
                          setSelectedGoogleEvent(event);
                          setIsGoogleEventModalOpen(true);
                        }}
                        className={`rounded-md border p-1 text-[11px] font-medium overflow-hidden truncate cursor-pointer transition-all hover:shadow-md ${
                          isDarkMode
                            ? "bg-blue-950/80 border-blue-700 text-white hover:bg-blue-900/90"
                            : "bg-blue-50 border-blue-200 text-blue-900 hover:bg-blue-100"
                        }`}
                      >
                        <div className="truncate">{event.summary}</div>
                        <div className="mt-1 text-[10px]">
                          {event.time} • {event.duration}m
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Block-out periods with full cards */}
                {dayBlockOuts.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayBlockOuts.length}, 1fr)`,
                      gap: "2px",
                      marginTop: dayBookings.length > 0 ? "4px" : "0",
                    }}
                  >
                    {dayBlockOuts.map((blockOut) => (
                      <div key={blockOut.id} className="min-w-0">
                        <BlockOutCard
                          blockOut={blockOut}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditBlockOut}
                          enableResize={true}
                          onDurationChange={onBlockOutDurationChange}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Tasks */}
                {dayTasks.length > 0 && (
                  <div
                    className="w-full"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${dayTasks.length}, 1fr)`,
                      gap: "2px",
                      marginTop:
                        dayBookings.length > 0 || dayBlockOuts.length > 0
                          ? "4px"
                          : "0",
                    }}
                  >
                    {dayTasks.map((task) => (
                      <div key={task.id} className="min-w-0">
                        <TaskCard
                          task={task}
                          size="micro"
                          isDarkMode={isDarkMode}
                          onEdit={onEditTask}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <GoogleEventDetailsModal
          isDarkMode={isDarkMode}
          event={selectedGoogleEvent}
          isOpen={isGoogleEventModalOpen}
          onClose={() => setIsGoogleEventModalOpen(false)}
        />
      </>
    );
  }

  // Week view (default - no day grouping)
  return (
    <>
      <BookingModal
        trigger={
          <div
            className={`p-2 h-12 border-r transition-all duration-200 relative overflow-visible ${
              isNonWorkingHours
                ? isDarkMode
                  ? "bg-gray-800 cursor-not-allowed pointer-events-none border-gray-700"
                  : "bg-gray-200 cursor-not-allowed pointer-events-none border-gray-300"
                : isDragOver
                  ? isDarkMode
                    ? "bg-blue-800 border-blue-500 border-2 ring-2 ring-blue-500 cursor-pointer"
                    : "bg-blue-100 border-blue-400 border-2 ring-2 ring-blue-400 cursor-pointer"
                  : isDarkMode
                    ? "border-gray-600 hover:bg-gray-700 cursor-pointer"
                    : "border-gray-200 hover:bg-gray-50 cursor-pointer"
            }`}
            style={
              isNonWorkingHours
                ? {
                    backgroundImage: `repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 8px,
                    ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 8px,
                    ${isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.04)"} 16px
                  )`,
                  }
                : undefined
            }
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div
              style={{
                display: hourBookings.length > 0 ? "grid" : "block",
                gridTemplateColumns:
                  hourBookings.length > 0
                    ? `repeat(${hourBookings.length}, 1fr)`
                    : undefined,
                gap: hourBookings.length > 0 ? "4px" : undefined,
              }}
            >
              {hourBookings.map((booking) => (
                <div key={booking.id} className="relative min-w-0 w-full">
                  <BookingCard
                    booking={booking}
                    serviceColors={serviceColors}
                    serviceNameMap={serviceNameMap}
                    serviceColorMap={serviceColorMap}
                    onEdit={onEditBooking}
                    onDelete={onDeleteBooking}
                    onStartConsultation={onStartConsultation}
                    pharmacists={pharmacists}
                    rooms={rooms}
                    size="small"
                    isDarkMode={isDarkMode}
                    enableResize={true}
                    onDurationChange={onDurationChange}
                    showNotification={notifiedBookingId === booking.id}
                    notificationType={notificationType}
                  />
                </div>
              ))}
            </div>

            {/* Block-out periods */}
            <div
              style={{
                display: hourBlockOuts.length > 0 ? "grid" : "block",
                gridTemplateColumns:
                  hourBlockOuts.length > 0
                    ? `repeat(${hourBlockOuts.length}, 1fr)`
                    : undefined,
                gap: hourBlockOuts.length > 0 ? "4px" : undefined,
                marginTop: hourBookings.length > 0 ? "4px" : "0",
              }}
            >
              {hourBlockOuts.map((blockOut) => (
                <div key={blockOut.id} className="min-w-0">
                  <BlockOutCard
                    blockOut={blockOut}
                    size="small"
                    isDarkMode={isDarkMode}
                    onEdit={onEditBlockOut}
                    enableResize={true}
                    onDurationChange={onBlockOutDurationChange}
                  />
                </div>
              ))}
            </div>
          </div>
        }
        onSave={onCreateBooking}
        pharmacists={pharmacists}
        rooms={rooms}
        initialDate={dateString}
        initialTime={initialTime}
        initialPharmacist={
          columnMode === "practitioner" ? columnValue : undefined
        }
        initialServiceType={
          columnMode === "services" ? (columnValue as any) : undefined
        }
        isDarkMode={isDarkMode}
      />
      <GoogleEventDetailsModal
        isDarkMode={isDarkMode}
        event={selectedGoogleEvent}
        isOpen={isGoogleEventModalOpen}
        onClose={() => setIsGoogleEventModalOpen(false)}
      />
    </>
  );
}
